import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  Bug, Loader2, MapPin, Route as RouteIcon, User as UserIcon, Clock, Hash, Monitor,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const STATUS_META = {
  nieuw: { label: 'Nieuw', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  bezig: { label: 'Bezig', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  opgelost: { label: 'Opgelost', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  afgewezen: { label: 'Afgewezen', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const STATUSES = ['nieuw', 'bezig', 'opgelost', 'afgewezen'];

function fmt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function Probleemmeldingen() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bug-reports', {
        params: { status: filter === 'all' ? undefined : filter },
      });
      setItems(data.items || []);
    } catch (e) {
      toast({ title: 'Meldingen laden mislukt', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/bug-reports/${id}`, { status });
      setItems((cur) => cur.map((r) => (r.id === id ? { ...r, status } : r)));
      toast({ title: `Status → ${STATUS_META[status]?.label || status}` });
      if (filter !== 'all' && filter !== status) {
        setItems((cur) => cur.filter((r) => r.id !== id));
      }
    } catch (e) {
      toast({ title: 'Status wijzigen mislukt', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="page-probleemmeldingen">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Bug size={20} className="text-brand-navy" /> Probleemmeldingen
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Gestructureerde bug-meldingen uit de app en het portaal.
          </p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white text-xs">
          {['all', ...STATUSES].map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`pm-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 border-r last:border-r-0 border-slate-200 transition-colors ${
                filter === f ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f === 'all' ? 'Alle' : STATUS_META[f].label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="pm-list">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Bug size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen probleemmeldingen.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.nieuw;
              return (
                <li key={r.id} data-testid={`pm-item-${r.id}`} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Badge variant="outline" className={`text-[10px] border ${meta.cls}`}>{meta.label}</Badge>
                        <Badge variant="outline" className="text-[10px] border-slate-200 bg-slate-50 text-slate-500 capitalize">
                          {r.source === 'portaal' ? 'Portaal' : 'Manager-app'}
                        </Badge>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1">
                          <Clock size={11} /> {fmt(r.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.omschrijving}</p>
                      {r.verwacht && (
                        <p className="mt-1.5 text-xs text-slate-500">
                          <span className="font-medium text-slate-600">Verwacht:</span> {r.verwacht}
                        </p>
                      )}
                      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                        {r.route && <span className="flex items-center gap-1"><RouteIcon size={11} /> {r.route}</span>}
                        {r.rol && <span className="flex items-center gap-1"><UserIcon size={11} /> {r.rol}</span>}
                        {r.location_scope && <span className="flex items-center gap-1"><MapPin size={11} /> {r.location_scope}</span>}
                        {r.correlation_ref && (
                          <span className="flex items-center gap-1 font-mono text-orange-600" data-testid={`pm-ref-${r.id}`}>
                            <Hash size={11} /> ref: {r.correlation_ref}
                          </span>
                        )}
                        {r.user_agent && (
                          <span className="flex items-center gap-1 truncate max-w-[240px]" title={r.user_agent}>
                            <Monitor size={11} /> {r.user_agent}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)} disabled={busy === r.id}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`pm-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="text-xs" data-testid={`pm-status-opt-${s}`}>
                              {STATUS_META[s].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
